import React, { useEffect, useRef, useState } from 'react';
import { IDialogConfig } from '../../../types/dialog';
import { useTranslation } from 'react-i18next';
import {
  IMyWatchListGeneralForm,
  IMyWatchListObject,
} from '../../../types/list';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { MarsApiService as api } from '../../../api/mars-api-service';
import { useLoaderContext } from '../../../contexts/LoaderContext';
import toast from '../../../utils/toast';
import { MarsApiConfig as apiConfig } from '../../../api/mars-api-config';
import Button from 'devextreme-react/button';
import ConfirmDialog from '../../../components/common/confirm/ConfirmDialog';
import MyWatchListListFormGeneralFields from '../../../components/my-watch-list/my-watch-list-general-field/MyWatchListFormGeneralField';
import { getEmployeeId } from '../../../utils/jwt-decode';
import FleetVesselList from '../../../components/my-watch-list/fleet-vessel-list/FleetVesselList';

export default function EditMyWatchList() {
  const [confirmSaveDialog, setConfirmSaveDialog] = useState(false);
  const [myWatchListId, setMyWatchListId] = useState<any>();
  const [editMyWatchListId, setEditMyWatchListId] = useState(myWatchListId);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setIsLoading } = useLoaderContext();
  const [title, setTitle] = useState('');
  const [myWatchListDetails, setMyWatchListDetails] =
    useState<IMyWatchListObject>();
  const [confirmBackDialog, setConfirmBackDialog] = useState(false);
  const location = useLocation();
  const employeeId = getEmployeeId();

  const onClickSave = (e: any) => {
    if (e.validationGroup.validate().isValid) {
      setConfirmSaveDialog(true);
    }
    return;
  };

  const defaultMyWatchListFormValues: IMyWatchListGeneralForm = {
    fleetname: '',
    color: '',
    active: false,
  };

  const listGeneralForm = useForm({
    defaultValues: defaultMyWatchListFormValues,
  });

  const { getValues } = listGeneralForm;
  const listGeneralFormValue: IMyWatchListGeneralForm = getValues();

  //on click back button
  const confirmBackDialogConfig: IDialogConfig = {
    id: 'confirmBackEditList',
    content: t('toast.confirmBackEditList'),
    handleSubmit: () => {
      setConfirmBackDialog(false);
      navigate('/my-watch-list');
    },
    handleClose: () => {
      setConfirmBackDialog(false);
    },
  };

  const confirmSaveDialogConfig: IDialogConfig = {
    id: 'confirmSave',
    handleSubmit: async () => {
      setConfirmSaveDialog(false);
      if (editMyWatchListId) {
        saveEditedMyWatchList();
      } else {
        saveMyWatchList();
      }
    },
    handleClose: () => {
      setConfirmSaveDialog(false);
    },
  };

  const saveMyWatchList = async () => {
    const myWatchListObject: IMyWatchListObject = {
      fleetName: listGeneralFormValue.fleetname,
      color: listGeneralFormValue.color,
      active: listGeneralFormValue.active,
      employeeId: employeeId,
    };

    const obj: any = { ...myWatchListObject };

    await api
      .post({ url: apiConfig.myWatchList, data: obj }, setIsLoading)
      .then((response) => {
        toast.success({
          title: t('toast.listAdded'),
        });
        navigate('/my-watch-list/edit', {
          replace: true,
          state: { fleetId: response },
        });
      });
  };

  const saveEditedMyWatchList = async () => {
    const myWatchListObject: IMyWatchListObject = {
      fleetId: editMyWatchListId,
      fleetName: listGeneralFormValue.fleetname,
      color: listGeneralFormValue.color,
      active: listGeneralFormValue.active,
    };
    await api
      .put(
        { url: apiConfig.myWatchList, data: myWatchListObject },
        setIsLoading
      )
      .then((response) => {
        setEditMyWatchListId(editMyWatchListId);
        toast.success({
          title: t('toast.listAdded'),
        });
      });
    await getMyWatchListById();
  };

  //view list to edit
  const getMyWatchListById = async () => {
    await api
      .get({ url: apiConfig.myWatchListId + editMyWatchListId })
      .then((res) => {
        setMyWatchListDetails(res);
      });
  };

  useEffect(() => {
    listGeneralForm.reset();
    if (myWatchListDetails) {
      setTitle(myWatchListDetails.fleetName);
      listGeneralForm.reset(
        {
          fleetname: myWatchListDetails.fleetName,
          color: myWatchListDetails.color,
          active: myWatchListDetails.active,
        },
        { keepDirty: true }
      );
    }
  }, [myWatchListDetails]);

  useEffect(() => {
    if (location.state) {
      const { fleetId }: any = location.state;
      setMyWatchListId(fleetId);
      setEditMyWatchListId(fleetId);
    }
  }, [location.state]);

  useEffect(() => {
    if (myWatchListId) {
      getMyWatchListById();
    }
  }, [myWatchListId]);

  const { isDirty } = listGeneralForm.formState;

  const handleBackClick = () => {
    if (isDirty && !myWatchListId) {
      setConfirmBackDialog(true);
    } else {
      navigate('/my-watch-list');
    }
  };

  const handleGetMyWatchListByIdFromExcel = async () => {
    await getMyWatchListById(); // still call the original
  };

  return (
    <div className="inner-wrapper">
      <ul aria-label="breadcrumb" className="m-c-bredcrumb">
        <li>
          <a className="active">{t('headers.mywatchlist')}</a>
        </li>
      </ul>
      <div className="m-l-inner-page-header m-l-srv-btm">
        <div className="m-l-inner-page-header-left-block">
          <div className="m-l-page-main-heading">
            {editMyWatchListId ? title : t('headers.addMyWatchList')}
          </div>
        </div>
        <div className="m-l-inner-page-header-right-block">
          <Button
            className="app-c-btn app-c-btn--secondary"
            elementAttr={{ 'data-testid': 'backButton' }}
            onClick={handleBackClick}
          >
            {t('buttons.back')}
          </Button>
          <Button
            className="app-c-btn app-c-btn--primary min-btn-width"
            elementAttr={{ 'data-testid': 'saveButton' }}
            onClick={onClickSave}
          >
            {t('buttons.save')}
          </Button>
        </div>
      </div>
      <div className="m-l-inner-page-body-wrap">
        <div className="m-l-inner-page-body">
          <div className="m-l-form-panel-block">
            <div className="m-l-form-panel-body-block ">
              <FormProvider {...listGeneralForm}>
                <MyWatchListListFormGeneralFields />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
      <ConfirmDialog
        dialogConfig={confirmBackDialogConfig}
        isOpen={confirmBackDialog}
      />
      <ConfirmDialog
        dialogConfig={confirmSaveDialogConfig}
        isOpen={confirmSaveDialog}
      />
      {editMyWatchListId ? (
        <FleetVesselList
          fleetId={myWatchListId}
          getMyWatchListById={handleGetMyWatchListByIdFromExcel}
          fleetName={listGeneralForm.getValues('fleetname')}
        />
      ) : null}
    </div>
  );
}
